import React from "react";
import styled from "styled-components";
import { colors, typography } from "../styles/designSystem";
import { SubsectionHeadline } from "../components/Typography";

import ConnectImg from "../pngs/DSTool/Connect.png";
import LinkFigmaImg from "../pngs/DSTool/Link_Figma.png";
import SelectComponentsImg from "../pngs/DSTool/Selct Components.png";
import DesignDataImg from "../pngs/DSTool/Design_Data.png";
import SelectOutputImg from "../pngs/DSTool/Select Output.png";
import StartAppImg from "../pngs/DSTool/Start_app_preview.png";

const PageWrapper = styled.div`
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem 1.5rem 6rem;
`;

const HeroSection = styled.section`
    text-align: center;
    padding: 3rem 0 3rem;
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
    margin-bottom: 4rem;
`;

const HeroLabel = styled.span`
    display: inline-block;
    font-size: ${typography.fontSize[100]};
    font-weight: ${typography.fontWeight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: ${colors.indigo[500]};
    background: ${colors.indigo[500]}15;
    padding: 6px 16px;
    border-radius: 999px;
    margin-bottom: 1.5rem;
`;

const HeroTitle = styled.h1`
    font-size: ${typography.fontSize[900]};
    font-weight: ${typography.fontWeight.bold};
    color: ${({ theme }) => theme.text};
    line-height: ${typography.lineHeight[200]};
    margin: 0 0 1.5rem;

    @media (max-width: 768px) {
        font-size: ${typography.fontSize[700]};
    }
`;

const StatusBadge = styled.span`
    display: inline-block;
    font-size: ${typography.fontSize[100]};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.amber[600]};
    background: ${colors.amber[500]}15;
    padding: 4px 12px;
    border-radius: 999px;
    margin-top: 1rem;
`;

const HeroSubtitle = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    max-width: 600px;
    margin: 0 auto;
`;

const ContentSection = styled.section`
    margin-bottom: 4.5rem;
`;

const SectionTitle = styled(SubsectionHeadline)`
    margin-bottom: 1.5rem;
`;

const Paragraph = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 1.25rem;
`;

const TechGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 1rem;
`;

const TechChip = styled.span`
    font-size: ${typography.fontSize[100]};
    font-weight: ${typography.fontWeight.medium};
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.cardBackground};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 999px;
    padding: 0.4rem 1rem;
`;

const ScreenshotFull = styled.img`
    width: 100%;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    margin: 2.5rem 0;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const ImageGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin: 2.5rem 0;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const ScreenshotGrid = styled.img`
    width: 100%;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const ImageCaption = styled.span`
    display: block;
    text-align: center;
    font-size: ${typography.fontSize[100]};
    color: ${({ theme }) => theme.subtleText};
    margin-top: 0.75rem;
`;

const FigmaToCode = () => {
    return (
        <PageWrapper>
            <HeroSection>
                <HeroLabel>Developer Tool</HeroLabel>
                <HeroTitle>Figma to Code</HeroTitle>
                <HeroSubtitle>
                    A desktop Electron app that turns your Figma design system into
                    production-ready React + Tailwind components — and generates an
                    AI-readable manifest so tools like Claude Code and Cursor can build
                    with your real components, not hallucinated ones.
                </HeroSubtitle>
                <StatusBadge>In Progress</StatusBadge>
            </HeroSection>

            <ScreenshotFull src={ConnectImg} alt="Figma to Code — Connect to Figma with MCP" />

            <ContentSection>
                <SectionTitle>The Problem</SectionTitle>
                <Paragraph>
                    The handoff from Figma to code is one of the biggest bottlenecks in product
                    development. Designers create components in Figma. Developers recreate them
                    in code. The translation is manual, error-prone, and slow — especially for
                    design systems with dozens of components and variants.
                </Paragraph>
                <Paragraph>
                    Existing tools either generate unusable code or miss the nuance of how
                    production components actually need to work: responsive behavior, proper
                    prop APIs, token-based styling, and accessibility.
                </Paragraph>
            </ContentSection>

            <ImageGrid>
                <div>
                    <ScreenshotGrid src={LinkFigmaImg} alt="Import Figma file via URL" />
                    <ImageCaption>Import any Figma file via URL</ImageCaption>
                </div>
                <div>
                    <ScreenshotGrid src={SelectComponentsImg} alt="Select components from Figma file tree" />
                    <ImageCaption>Browse and select components from the file tree</ImageCaption>
                </div>
            </ImageGrid>

            <ContentSection>
                <SectionTitle>How It Works</SectionTitle>
                <Paragraph>
                    Figma to Code is a 5-step wizard that connects to Figma via MCP server,
                    reads your design system's component structure, lets you preview the
                    design data, then generates clean React + Tailwind code with configurable
                    output options.
                </Paragraph>
                <Paragraph>
                    It understands the semantic structure of a Figma component — its layers,
                    constraints, variants, and properties — and maps them to the equivalent
                    React + Tailwind architecture. Not pixel-to-code screenshots, but real
                    component generation.
                </Paragraph>
            </ContentSection>

            <ImageGrid>
                <div>
                    <ScreenshotGrid src={DesignDataImg} alt="Preview design data before generating code" />
                    <ImageCaption>Preview design data before generation</ImageCaption>
                </div>
                <div>
                    <ScreenshotGrid src={SelectOutputImg} alt="Configure output options — library, format, TypeScript" />
                    <ImageCaption>Configure output — library, format, TypeScript</ImageCaption>
                </div>
            </ImageGrid>

            <ScreenshotFull src={StartAppImg} alt="Generated component library with next steps" />

            <ContentSection>
                <SectionTitle>What Gets Generated</SectionTitle>
                <Paragraph>
                    The output is a full component library — individual .tsx files for each
                    component, ready to run with npm install and npm run dev. Every component
                    is generated with clean prop APIs, proper TypeScript types, and Tailwind
                    utility classes that map directly to the Figma design tokens.
                </Paragraph>
            </ContentSection>

            <ContentSection>
                <SectionTitle>Built for AI-First Development</SectionTitle>
                <Paragraph>
                    The real power is what happens after generation. Alongside the component
                    files, Figma to Code generates a component-manifest.json — a structured
                    map of every component, its props, accepted values, and usage patterns.
                </Paragraph>
                <Paragraph>
                    Point Claude Code, Cursor, or any AI coding tool at the manifest and it
                    understands your entire component library. Ask it to "build a pricing page
                    using our design system" and it knows exactly which components exist, what
                    props they accept, and how to compose them correctly. No hallucinated
                    components. No made-up prop names. Just your real design system, usable
                    by AI out of the box.
                </Paragraph>
                <Paragraph>
                    This is the bridge between design systems and AI-driven development —
                    your Figma components become the building blocks that both humans and
                    AI agents use to ship UI.
                </Paragraph>
            </ContentSection>

            <ContentSection>
                <SectionTitle>Tech Stack</SectionTitle>
                <Paragraph>
                    Built as a cross-platform desktop application using Electron with a
                    React-based UI and Figma MCP integration.
                </Paragraph>
                <TechGrid>
                    <TechChip>Electron</TechChip>
                    <TechChip>React</TechChip>
                    <TechChip>Node.js</TechChip>
                    <TechChip>Figma MCP</TechChip>
                    <TechChip>Tailwind CSS</TechChip>
                    <TechChip>TypeScript</TechChip>
                </TechGrid>
            </ContentSection>
        </PageWrapper>
    );
};

export default FigmaToCode;
